import "./assignment-edit-pane.styl";
import template from "./assignment-edit-pane.pug";
import { ViewModel } from "../../../../../vm/viewmodel";
import type { Observable, ObservableArray, PureComputed } from "knockout";
import { observableArray } from "knockout";
import ko, { pureComputed, observable } from "knockout";
import type { CanRequestSize } from "../../../common/sized-modal";
import type { ComponentArgs } from "@/lib/components/common";
import { ProjectStore } from "@/stores/project-store.core";
import type { SerializedProject } from "@laborchart-modules/common/dist/rethink/serializers/project-serializer";
import type {
   SerializedCostCode,
   SerializedCostCodeLabel,
   SerializedPerson,
} from "@laborchart-modules/common/dist/rethink/serializers";
import { getAttachedDate, getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import type { StagedAssignment } from "../../assignment-details-modal";
import { AssignmentDetailsModalType } from "../../assignment-details-modal";
import type { ProjectEditorElementParams } from "@/lib/components/modals/editors/project-editor-element/project-editor-element";
import { ProjectEditorElement } from "@/lib/components/modals/editors/project-editor-element/project-editor-element";
import type { CostCodeEditorElementParams } from "@/lib/components/modals/editors/cost-code-editor-element/cost-code-editor-element";
import { CostCodeEditorElement } from "@/lib/components/modals/editors/cost-code-editor-element/cost-code-editor-element";
import type { CostCodeLabelEditorElementParams } from "@/lib/components/modals/editors/cost-code-label-editor-element/cost-code-label-editor-element";
import { CostCodeLabelEditorElement } from "@/lib/components/modals/editors/cost-code-label-editor-element/cost-code-label-editor-element";
import type { StartDayEditorElementParams } from "@/lib/components/modals/editors/start-day-editor-element/start-day-editor-element";
import {
   StartDayEditorElement,
   StartDayOption,
} from "@/lib/components/modals/editors/start-day-editor-element/start-day-editor-element";
import type { EndDayEditorElementParams } from "@/lib/components/modals/editors/end-day-editor-element/end-day-editor-element";
import {
   EndDayEditorElement,
   EndDayOption,
} from "@/lib/components/modals/editors/end-day-editor-element/end-day-editor-element";
import type {
   WorkDaySelection,
   WorkDayEditorElementParams,
} from "@/lib/components/modals/editors/work-day-editor-element/work-day-editor-element";
import {
   WorkDayEditorElement,
   WorkDay,
} from "@/lib/components/modals/editors/work-day-editor-element/work-day-editor-element";
import type { AllocationTypeEditorElementParams } from "@/lib/components/modals/editors/allocation-type-editor-element/allocation-type-editor-element";
import {
   AllocationType,
   AllocationTypeEditorElement,
} from "@/lib/components/modals/editors/allocation-type-editor-element/allocation-type-editor-element";
import type { NestedComputedAssignmentSupportData } from "@/stores/assignment-2-store.core";
import { DateUtils } from "@/lib/utils/date";
import type { WorkHoursEditorElementParams } from "@/lib/components/modals/editors/work-hours-editor-element/work-hours-editor-element";
import { WorkHoursEditorElement } from "@/lib/components/modals/editors/work-hours-editor-element/work-hours-editor-element";
import type { PercentAllocationEditorElementParams } from "@/lib/components/modals/editors/percent-allocation-editor-element/percent-allocation-editor-element";
import { PercentAllocationEditorElement } from "@/lib/components/modals/editors/percent-allocation-editor-element/percent-allocation-editor-element";
import type { NullableValueCustomFieldInstance } from "@/lib/components/modals/editors/custom-field-editor-element/custom-field-editor-element";
import { CustomFieldEditorElement } from "@/lib/components/modals/editors/custom-field-editor-element/custom-field-editor-element";
import type {
   AssignmentPaySplit,
   CustomFieldValue,
} from "@laborchart-modules/common/dist/rethink/schemas";
import type { SerializedFindAssignment } from "@laborchart-modules/lc-core-api/dist/api/assignments/find-assignments";
import type { SerializedCustomField } from "@laborchart-modules/common/dist/rethink/serializers/custom-field-serializer";
import type { SerializedCustomFieldInstance } from "@laborchart-modules/common/dist/rethink/serializers/custom-field-instance-serializer";
import type { PersonEditorElementParams } from "../../../editors/person-editor-element/person-editor-element";
import { PersonEditorElement } from "../../../editors/person-editor-element/person-editor-element";
import { authManager } from "@/lib/managers/auth-manager";
import type { OvertimeCheckboxEditorElementParams } from "../../../editors/overtime-checkbox-editor-element/overtime-checkbox-editor-element";
import { OvertimeCheckboxEditorElement } from "../../../editors/overtime-checkbox-editor-element/overtime-checkbox-editor-element";
import type {
   OvertimeRates,
   OvertimeRatesEditorElementParams,
} from "../../../editors/overtime-rates-editor-element/overtime-rates-editor-element";
import { OvertimeRatesEditorElement } from "../../../editors/overtime-rates-editor-element/overtime-rates-editor-element";
import type { OvertimePaysplitEditorElementParams } from "../../../editors/overtime-paysplit-editor-element/overtime-paysplit-editor-element";
import { OvertimePaysplitEditorElement } from "../../../editors/overtime-paysplit-editor-element/overtime-paysplit-editor-element";
import type { StatusOption } from "@/lib/components/drop-downs/panes/status-drop-down-pane";
import type { StatusEditorElementParams } from "../../../editors/status-editor-element/status-editor-element";
import { StatusEditorElement } from "../../../editors/status-editor-element/status-editor-element";
import { PermissionLevel } from "@/models/permission-level";
import { getLastWorkDateInNumberOfWeeks, mutateForTimezone } from "@/lib/utils/date-2";

type DisplayOption = "block" | "none";

const enum AssignmentEditorName {
   ALLOCATION_TYPE_EDITOR = "allocation-type-editor",
   COST_CODE_EDITOR = "cost-code-editor",
   COST_CODE_LABEL_EDITOR = "cost-code-label-editor",
   END_DAY_EDITOR = "end-day-editor",
   JOB_TITLE_EDITOR = "job-title-editor",
   OVERTIME_CHECKBOX_EDITOR = "overtime-checkbox-editor",
   OVERTIME_PAYSPLIT_EDITOR = "overtime-paysplit-editor",
   OVERTIME_RATES_EDITOR = "overtime-rates-editor",
   PERCENT_ALLOCATION_EDITOR = "percent-allocation-editor",
   PERSON_EDITOR = "person-editor",
   PROJECT_EDITOR = "project-editor",
   START_DAY_EDITOR = "start-day-editor",
   STATUS_EDITOR = "status-editor",
   TAGS_EDITOR = "tags-editor",
   WORK_DAY_EDITOR = "work-day-editor",
   WORK_HOURS_EDITOR = "work-hours-editor",
   CUSTOM_FIELD_EDITOR = "custom-field-editor",
}

type AssignmentEditorData = {
   component: ComponentArgs<any>;
   display: PureComputed<DisplayOption>;
   isUsed: () => boolean;
   name: AssignmentEditorName;
   validationText: PureComputed<string | null>;
};

/*
   These default rates will be used in the situation where the 
   overtime configuration is not turned on at the company level,
   and a user activates overtime when creating/editing an assignment.
*/
const DEFAULT_OVERTIME_RATES = {
   sunday: 1,
   monday: 1,
   tuesday: 1,
   wednesday: 1,
   thursday: 1,
   friday: 1,
   saturday: 1,
};

class AssignmentEditPaneState {
   private readonly internalState: {
      allocationType: Observable<AllocationType>;
      endDayNumWeeks: Observable<number | null>;
      endDayOption: Observable<EndDayOption>;
      startDayOption: Observable<StartDayOption>;
   };

   //#region Nouns
   readonly person = pureComputed(() => this.stagedAssignment().person);
   readonly project = pureComputed(() => this.stagedAssignment().project);
   readonly costCode = pureComputed(() => this.stagedAssignment().category);
   readonly costCodeLabel = pureComputed(() => this.stagedAssignment().subcategory);
   readonly statusOption = pureComputed<StatusOption | null>(() => {
      const status = this.stagedAssignment().status;
      return status != null
         ? {
              abbreviation: status.abbreviation,
              color: status.color,
              id: status.id,
              name: status.name,
              sequence: status.sequence,
           }
         : null;
   });
   readonly startDay = pureComputed(() => {
      const startDay = this.stagedAssignment().start_day;
      return startDay ? getAttachedDate(startDay) : null;
   });
   readonly endDay = pureComputed(() => {
      const endDay = this.stagedAssignment().end_day;
      return endDay ? getAttachedDate(endDay) : null;
   });

   readonly costCodeOptions = pureComputed<SerializedCostCode[]>(() => {
      const project = this.project();
      return project != null ? project.categories : [];
   });
   readonly costCodeLabelOptions = pureComputed<SerializedCostCodeLabel[]>(() => {
      const costCode = this.costCode();
      return costCode != null ? costCode.subcategories : [];
   });
   //#endregion

   readonly allocationType = pureComputed(() => this.internalState.allocationType());
   readonly endDayNumWeeks = pureComputed(() => this.internalState.endDayNumWeeks());
   readonly endDayOption = pureComputed(() => this.internalState.endDayOption());
   readonly startDayOption = pureComputed(() => this.internalState.startDayOption());

   readonly assignment: PureComputed<SerializedFindAssignment | StagedAssignment>;
   readonly stagedAssignment: PureComputed<StagedAssignment>;
   readonly supportData: NestedComputedAssignmentSupportData;

   readonly updateAssignmentDetails: (update: Partial<StagedAssignment>) => void;

   // Non-writing helpers.
   readonly isWorkDay = (day: WorkDay) => this.stagedAssignment().work_days[day];

   constructor({
      assignment,
      stagedAssignment,
      supportData,
      updateAssignmentDetails,
   }: {
      assignment: PureComputed<SerializedFindAssignment | StagedAssignment>;
      stagedAssignment: PureComputed<StagedAssignment>;
      supportData: NestedComputedAssignmentSupportData;
      updateAssignmentDetails: (update: Partial<StagedAssignment>) => void;
   }) {
      this.internalState = {
         allocationType: observable(
            assignment().percent_allocated != null ? AllocationType.PERCENT : AllocationType.HOURS,
         ),
         endDayNumWeeks: observable<number | null>(supportData.companyTbdWeeks()),
         endDayOption: observable<EndDayOption>(EndDayOption.DATE),
         startDayOption: observable<StartDayOption>(StartDayOption.DATE),
      };
      this.assignment = assignment;
      this.stagedAssignment = stagedAssignment;
      this.supportData = supportData;
      this.updateAssignmentDetails = updateAssignmentDetails;
   }

   //#region Verbs
   setPerson = (person: SerializedPerson | null) => {
      if (person?.id == this.stagedAssignment().resource_id) return;

      this.updateAssignmentDetails({
         person: person,
         resource_id: person?.id ?? null,
      });
   };
   setProject = (project: SerializedProject | null) => {
      if (project?.id == this.stagedAssignment().project_id) return;

      this.updateAssignmentDetails({
         project: project,
         project_id: project?.id ?? null,
      });

      if (this.startDayOption() == StartDayOption.PROJECT_START) {
         // Trigger recomputing to ensure that start day is still correct.
         this.setStartDayOption(this.startDayOption());
      }
      if (project?.est_end_date != null && this.endDayOption() == EndDayOption.TBD) {
         this.setEndDayOption(EndDayOption.DATE);
      } else if (project?.est_end_date == null && this.endDayOption() == EndDayOption.PROJECT_END) {
         this.setEndDayOption(EndDayOption.DATE);
      } else if (project?.est_end_date != null && this.endDayOption() == EndDayOption.PROJECT_END) {
         this.setEndDayOption(EndDayOption.PROJECT_END);
      }
      if (
         this.allocationType() == AllocationType.HOURS &&
         (this.assignment().start_time == null || this.assignment().end_time == null)
      ) {
         // Trigger recomputing to auto-set start and end time if not already set.
         this.setAllocationType(this.allocationType());
      }

      if (project === null) {
         // Clear the cost code if project was cleared.
         this.setCostCode(null);
         return;
      }

      if (project.id != this.assignment().project_id) {
         // Clear the cost code if project was changed from original state.
         this.setCostCode(null);
      } else {
         const selectedCostCodeId = this.assignment().category_id;
         const selectedCostCode = selectedCostCodeId
            ? project.categories.find((category) => selectedCostCodeId == category.id) ?? null
            : null;
         this.setCostCode(selectedCostCode);
      }
   };
   setCostCode = (costCode: SerializedCostCode | null) => {
      if (costCode?.id == this.stagedAssignment().category_id) return;

      this.updateAssignmentDetails({
         category: costCode,
         category_id: costCode?.id ?? null,
      });

      if (costCode === null) {
         // Clear the cost code label if cost code was cleared.
         this.setCostCodeLabel(null);
         return;
      }

      if (costCode.id != this.assignment().category_id) {
         // Clear the cost code label if cost code was changed from original state.
         this.setCostCodeLabel(null);
      } else {
         // Reset the cost code label to the original value if
         // cost code value was reverted to original value.
         const selectedCostCodeLabelId = this.assignment().subcategory_id;
         const selectedCostCodeLabel = selectedCostCodeLabelId
            ? costCode.subcategories.find(
                 (subcategory) => selectedCostCodeLabelId == subcategory.id,
              ) ?? null
            : null;
         this.setCostCodeLabel(selectedCostCodeLabel);
      }
   };
   setCostCodeLabel = (costCodeLabel: SerializedCostCodeLabel | null) => {
      if (costCodeLabel?.id == this.stagedAssignment().subcategory_id) return;

      this.updateAssignmentDetails({
         subcategory: costCodeLabel,
         subcategory_id: costCodeLabel?.id ?? null,
      });
   };
   setStartDay = (startDay: Date | null) => {
      if (startDay) this.updateAssignmentDetails({ start_day: getDetachedDay(startDay) });
      else this.updateAssignmentDetails({ start_day: null });

      if ([EndDayOption.TBD, EndDayOption.WEEKS].includes(this.endDayOption()))
         // Trigger recomputing to ensure that end day is still correct.
         this.setEndDayOption(this.endDayOption());
   };
   setEndDay = (endDay: Date | null) => {
      if (endDay) this.updateAssignmentDetails({ end_day: getDetachedDay(endDay) });
      else this.updateAssignmentDetails({ end_day: null });
   };
   setEndDayNumWeeks = (endDayNumWeeks: number | null) => {
      this.internalState.endDayNumWeeks(endDayNumWeeks);
      if (endDayNumWeeks == null || endDayNumWeeks < 1 || endDayNumWeeks > 4000) return;

      const endDate = getLastWorkDateInNumberOfWeeks({
         numberOfWeeks: endDayNumWeeks,
         startDate: this.startDay() ?? new Date(),
         workDays: this.stagedAssignment().work_days,
      });

      this.setEndDay(endDate);
   };
   updateWorkDays = (workDayUpdate: Partial<WorkDaySelection>) => {
      const newWorkDays = {
         ...this.stagedAssignment().work_days,
         ...workDayUpdate,
      };
      this.updateAssignmentDetails({
         work_days: {
            [WorkDay.SUNDAY]: newWorkDays[WorkDay.SUNDAY],
            [WorkDay.MONDAY]: newWorkDays[WorkDay.MONDAY],
            [WorkDay.TUESDAY]: newWorkDays[WorkDay.TUESDAY],
            [WorkDay.WEDNESDAY]: newWorkDays[WorkDay.WEDNESDAY],
            [WorkDay.THURSDAY]: newWorkDays[WorkDay.THURSDAY],
            [WorkDay.FRIDAY]: newWorkDays[WorkDay.FRIDAY],
            [WorkDay.SATURDAY]: newWorkDays[WorkDay.SATURDAY],
         },
      });

      const endDayOption = this.endDayOption();
      if ([EndDayOption.TBD, EndDayOption.WEEKS].includes(endDayOption))
         // Trigger recomputing to ensure that end day is still a valid work day.
         this.setEndDayNumWeeks(this.endDayNumWeeks());
      if (this.startDayOption() == StartDayOption.PROJECT_START)
         // Trigger recomputing to ensure that start day is still a valid work day.
         this.setStartDayOption(this.startDayOption());
   };
   setStartTime = (startTime: number | null) => {
      this.updateAssignmentDetails({ start_time: startTime });
   };
   setEndTime = (endTime: number | null) => {
      this.updateAssignmentDetails({ end_time: endTime });
   };
   setPercentAllocation = (percentAllocation: number | null) => {
      this.updateAssignmentDetails({ percent_allocated: percentAllocation });
   };
   setStatus = (status: StatusOption | null) => {
      this.updateAssignmentDetails({
         status:
            status != null
               ? {
                    abbreviation: status.abbreviation,
                    color: status.color! ?? null,
                    company_id: authManager.companyId(),
                    id: status.id! ?? null,
                    name: status.name! ?? null,
                    sequence: status.sequence,
                 }
               : null,
         status_id: status?.id ?? null,
      });
   };

   setAllocationType = (allocationType: AllocationType) => {
      this.internalState.allocationType(allocationType);
      if (allocationType === AllocationType.HOURS) {
         this.setPercentAllocation(null);
         const project = this.project();
         this.setStartTime(this.assignment().start_time ?? project?.daily_start_time ?? null);
         this.setEndTime(this.assignment().end_time ?? project?.daily_end_time ?? null);
      }
      if (allocationType === AllocationType.PERCENT) {
         this.setStartTime(null);
         this.setEndTime(null);
         this.setPercentAllocation(this.assignment().percent_allocated ?? 100);
      }
   };
   setEndDayOption = (endDayOption: EndDayOption) => {
      this.internalState.endDayOption(endDayOption);

      if ([EndDayOption.TBD, EndDayOption.WEEKS].includes(endDayOption)) {
         const weeks =
            endDayOption == EndDayOption.TBD
               ? this.supportData.companyTbdWeeks()
               : this.endDayNumWeeks();
         this.setEndDayNumWeeks(weeks);
      } else if (endDayOption == EndDayOption.PROJECT_END) {
         const project = this.project();
         const projectEndDate =
            project?.est_end_date != null ? new Date(project.est_end_date) : new Date();
         mutateForTimezone(projectEndDate);
         this.setEndDay(projectEndDate);
      }
   };
   setStartDayOption = (startDayOption: StartDayOption) => {
      this.internalState.startDayOption(startDayOption);

      if (startDayOption === StartDayOption.PROJECT_START) {
         const project = this.project();
         const projectStartDate =
            project && project.start_date ? new Date(project.start_date) : new Date();
         mutateForTimezone(projectStartDate);

         // Get the offset needed to select the first work day on or after the project start date.
         const offset =
            [0, 1, 2, 3, 4, 5, 6].find((offset) =>
               this.isWorkDay((projectStartDate.getDay() + offset) % 7),
            ) ?? 0;
         const startDate =
            offset == 0 ? projectStartDate : DateUtils.incrementDate(projectStartDate, offset);
         this.setStartDay(startDate);
      }
   };

   setOvertimeCheckbox = (isUsingOvertime: boolean) => {
      this.updateAssignmentDetails({ overtime: isUsingOvertime });
      if (isUsingOvertime === true) {
         this.updateOvertimeRates({});
         this.updateOvertimePaysplit({});
      } else if (isUsingOvertime === false) {
         this.updateOvertimePaysplit(null);
         this.updateOvertimeRates(null);
      }
   };

   updateOvertimePaysplit = (overtimePaysplit: Partial<AssignmentPaySplit> | null) => {
      // This method should never be called if overtimeDayRates is null.
      if (this.supportData.overtimeDayRates == null) {
         // If it is called though, make sure to update state properly.
         this.updateAssignmentDetails({ pay_split: null });
         return;
      }
      const currentSplit = this.stagedAssignment().pay_split;
      if (overtimePaysplit == null) {
         this.updateAssignmentDetails({ pay_split: null });
      } else if (Object.keys(overtimePaysplit).length > 0) {
         const newSplit: AssignmentPaySplit = {
            overtime: currentSplit?.overtime ?? 0,
            straight: currentSplit?.straight ?? 0,
            unpaid: currentSplit?.unpaid ?? 0,
            ...overtimePaysplit,
         };
         this.updateAssignmentDetails({ pay_split: newSplit });
      } else {
         const startTime = this.stagedAssignment().start_time!;
         const endTime = this.stagedAssignment().end_time!;
         const totalTime = endTime >= startTime ? endTime - startTime : 24 + endTime - startTime;
         this.updateAssignmentDetails({
            pay_split: {
               overtime: 0,
               straight: totalTime,
               unpaid: 0,
            },
         });
      }
   };

   updateOvertimeRates = (overtimeRates: Partial<OvertimeRates> | null) => {
      const overtimeDayRates = this.supportData.overtimeDayRates() ?? DEFAULT_OVERTIME_RATES;

      if (overtimeRates != null) {
         const currentRates = this.stagedAssignment().overtime_rates;
         const newOvertimeRates = {
            0:
               currentRates == null || currentRates[0] == 0
                  ? overtimeDayRates.sunday
                  : currentRates[0],
            1:
               currentRates == null || currentRates[1] == 0
                  ? overtimeDayRates.monday
                  : currentRates[1],
            2:
               currentRates == null || currentRates[2] == 0
                  ? overtimeDayRates.tuesday
                  : currentRates[2],
            3:
               currentRates == null || currentRates[3] == 0
                  ? overtimeDayRates.wednesday
                  : currentRates[3],
            4:
               currentRates == null || currentRates[4] == 0
                  ? overtimeDayRates.thursday
                  : currentRates[4],
            5:
               currentRates == null || currentRates[5] == 0
                  ? overtimeDayRates.friday
                  : currentRates[5],
            6:
               currentRates == null || currentRates[6] == 0
                  ? overtimeDayRates.saturday
                  : currentRates[6],
            ...overtimeRates,
         };
         this.updateAssignmentDetails({ overtime_rates: newOvertimeRates });
      } else {
         this.updateAssignmentDetails({ overtime_rates: null });
      }
   };

   setCustomFieldValue = ({
      customField,
      value,
   }: {
      customField: SerializedCustomField;
      value: CustomFieldValue | null;
   }) => {
      const newInstance =
         value == null
            ? // Null values don't have instances.
              null
            : ({
                 field_id: customField.id,
                 type: customField.type,
                 value,
                 name: customField.name,
                 integration_name: customField.integration_name,
              } as SerializedCustomFieldInstance);
      const newFields = this.stagedAssignment()
         .custom_fields.filter((field) => field.field_id != customField.id)
         .concat(newInstance != null ? [newInstance] : []);
      this.updateAssignmentDetails({
         custom_fields: newFields,
      });
   };
   //#endregion
}

export type AssignmentEditPaneParams = {
   customFields: PureComputed<SerializedCustomField[]>;
   assignmentDetailsModalType: PureComputed<AssignmentDetailsModalType>;
   preventingSave: PureComputed<boolean>;
   assignment: PureComputed<SerializedFindAssignment | StagedAssignment>;
   setPreventingSave: (preventSave: boolean) => void;
   stagedAssignment: PureComputed<StagedAssignment>;
   supportData: NestedComputedAssignmentSupportData;
   updateAssignmentDetails: (update: Partial<StagedAssignment>) => void;
} & CanRequestSize;

export class AssignmentEditPane extends ViewModel implements CanRequestSize {
   private readonly state: AssignmentEditPaneState;

   readonly editorSections: Array<ObservableArray<AssignmentEditorData>>;

   readonly customFields: AssignmentEditPaneParams["customFields"];
   readonly assignmentDetailsModalType: AssignmentEditPaneParams["assignmentDetailsModalType"];
   readonly requestSize: AssignmentEditPaneParams["requestSize"];

   readonly projectPlaceholder: Observable<string>;

   readonly preventingSave: PureComputed<boolean>;
   readonly setPreventingSave: (preventSave: boolean) => void;

   readonly enabledCustomFieldEditors: ObservableArray<AssignmentEditorData>;
   readonly disabledCustomFieldEditors: ObservableArray<AssignmentEditorData>;
   readonly getCustomFieldEditors = (integrationOnly: boolean): AssignmentEditorData[] => {
      const customFields = this.customFields();

      const editorFactories = customFields
         .filter((field) => field.integration_only == integrationOnly)
         .map((field) => {
            const customFieldInstance = pureComputed<NullableValueCustomFieldInstance>(() => {
               const exisitingCustomFieldInstance =
                  this.state
                     .stagedAssignment()
                     .custom_fields.find((instance) => instance.field_id == field.id) ?? null;
               if (exisitingCustomFieldInstance != null) {
                  return exisitingCustomFieldInstance as NullableValueCustomFieldInstance;
               }
               return {
                  field_id: field.id,
                  integration_name: field.integration_name,
                  name: field.name,
                  type: field.type,
                  value: null,
               };
            });
            return CustomFieldEditorElement.factory({
               customField: field,
               customFieldInstance,
               invalidInputMessage: pureComputed<string | null>(() => null),
               isDisabled: field.integration_only,
               onChange: (value) => {
                  this.state.setCustomFieldValue({
                     customField: field,
                     value,
                  });
               },
               requestSize: () => {},
            });
         });

      return editorFactories.map<AssignmentEditorData>((component) => ({
         component,
         display: pureComputed<DisplayOption>(() => "block" as const),
         isUsed: () => true,
         name: AssignmentEditorName.CUSTOM_FIELD_EDITOR,
         validationText: pureComputed<string | null>(() => null),
      }));
   };

   readonly showIntegrationLockedCustomFields = pureComputed(() => {
      return [
         AssignmentDetailsModalType.EDIT_ASSIGNMENT,
         AssignmentDetailsModalType.FILL_REQUEST,
      ].includes(this.assignmentDetailsModalType());
   });

   private readonly editorList: AssignmentEditorData[];

   constructor({
      customFields,
      assignmentDetailsModalType,
      preventingSave,
      assignment,
      requestSize,
      setPreventingSave,
      stagedAssignment,
      supportData,
      updateAssignmentDetails,
   }: AssignmentEditPaneParams) {
      super(template());
      this.state = new AssignmentEditPaneState({
         assignment,
         stagedAssignment,
         supportData,
         updateAssignmentDetails,
      });
      this.assignmentDetailsModalType = assignmentDetailsModalType;
      this.preventingSave = preventingSave;
      requestSize({
         width: 720,
         height: 500,
      });
      this.requestSize = requestSize;

      this.setPreventingSave = setPreventingSave;

      this.customFields = customFields;
      this.projectPlaceholder = observable("Loading...");

      const sectionedEditorList = this.getSectionedEditorList();
      this.editorList = sectionedEditorList.reduce((acc, cur) => [...acc, ...cur], []);
      this.editorSections = sectionedEditorList.map((section) => {
         return observableArray(section.filter((data) => data.isUsed()));
      });
      this.enabledCustomFieldEditors = observableArray(this.getCustomFieldEditors(false));
      this.disabledCustomFieldEditors = observableArray(this.getCustomFieldEditors(true));

      this.load();

      this.setPreventingSave(!this.isAllValid());
      this.isAllValid.subscribe((isAllValid) => {
         this.setPreventingSave(!isAllValid);
      });
   }

   async load(): Promise<void> {
      // TODO: Load status tracking.
      try {
         await Promise.all([this.loadProjectData()]);
      } catch {
         // TODO: Additional error handling.
         // The stores do basic error handling already, so test
         // any additional handling to make sure error messages
         // are not duplicated.
      }
   }

   private async loadProjectData(): Promise<void> {
      const projectId = this.state.stagedAssignment().project_id;
      if (projectId == null) return;
      try {
         // Get project data and update project state.
         const selectedProject = (await ProjectStore.getProject(projectId).payload).data;
         this.state.setProject(selectedProject);
      } catch (error) {
         this.state.setProject(null);
         throw error;
      }
   }

   startDayEditorFactory = (): ComponentArgs<StartDayEditorElementParams> => {
      return StartDayEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.START_DAY_EDITOR)!
               .validationText();
         }),
         onStartDayChange: (startDay) => {
            this.state.setStartDay(startDay);
         },
         onStartDayOptionChange: (startDayOption) => {
            this.state.setStartDayOption(startDayOption);
         },
         requestSize: () => {},
         startDay: this.state.startDay,
         startDayOption: this.state.startDayOption,
         title: "Start Day",
      });
   };

   endDayEditorFactory = (): ComponentArgs<EndDayEditorElementParams> => {
      return EndDayEditorElement.factory({
         endDay: this.state.endDay,
         endDayNumWeeks: this.state.endDayNumWeeks,
         endDayOption: this.state.endDayOption,
         projectHasEndDay: pureComputed(
            () => this.state.stagedAssignment().project?.est_end_date != null,
         ),
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.END_DAY_EDITOR)!
               .validationText();
         }),
         onEndDayChange: (endDay) => {
            this.state.setEndDay(endDay);
         },
         onEndDayNumWeeksChange: (endDayNumWeeks) => {
            this.state.setEndDayNumWeeks(endDayNumWeeks);
         },
         onEndDayOptionChange: (endDayOption) => {
            this.state.setEndDayOption(endDayOption);
         },
         requestSize: () => {},
         title: "End Day",
      });
   };

   projectEditorFactory = (): ComponentArgs<ProjectEditorElementParams> => {
      return ProjectEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.PROJECT_EDITOR)!
               .validationText();
         }),
         onChange: (project) => {
            this.state.setProject(project);
         },
         project: this.state.project,
         title: "Project",
         requestSize: () => {},
      });
   };

   personEditorFactory = (): ComponentArgs<PersonEditorElementParams> => {
      return PersonEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.PERSON_EDITOR)!
               .validationText();
         }),
         onChange: (person) => {
            this.state.setPerson(person);
         },
         person: this.state.person,
         title: "Person",
         requestSize: () => {},
      });
   };

   statusEditorFactory = (): ComponentArgs<StatusEditorElementParams> => {
      return StatusEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.STATUS_EDITOR)!
               .validationText();
         }),
         onChange: (status) => {
            this.state.setStatus(status);
         },
         status: this.state.statusOption,
         title: "Status",
         requestSize: () => {},
      });
   };

   costCodeEditorFactory = (): ComponentArgs<CostCodeEditorElementParams> => {
      return CostCodeEditorElement.factory({
         onChange: (costCode) => {
            this.state.setCostCode(costCode);
         },
         costCode: this.state.costCode,
         costCodeOptions: this.state.costCodeOptions,
         isDisabled: pureComputed(
            () => this.state.project() == null || this.state.costCodeOptions().length == 0,
         ),
         placeholder: pureComputed<string | null>(() =>
            this.state.project() == null
               ? "Project must be selected first."
               : this.state.costCodeOptions().length == 0
               ? "This project has no category options."
               : null,
         ),
         title: "Category",
         requestSize: () => {},
      });
   };

   costCodeLabelEditorFactory = (): ComponentArgs<CostCodeLabelEditorElementParams> => {
      return CostCodeLabelEditorElement.factory({
         onChange: (costCodeLabel) => {
            this.state.setCostCodeLabel(costCodeLabel);
         },
         costCodeLabel: this.state.costCodeLabel,
         costCodeLabelOptions: this.state.costCodeLabelOptions,
         isDisabled: pureComputed(
            () => this.state.costCode() == null || this.state.costCodeLabelOptions().length == 0,
         ),
         placeholder: pureComputed<string | null>(() =>
            this.state.costCode() == null
               ? "Category must be selected first."
               : this.state.costCodeLabelOptions().length == 0
               ? "This category has no subcategory options."
               : null,
         ),
         title: "Subcategory",
         requestSize: () => {},
      });
   };

   workDayEditorFactory = (): ComponentArgs<WorkDayEditorElementParams> => {
      return WorkDayEditorElement.factory({
         onChange: (workDays) => {
            this.state.updateWorkDays(workDays);
         },
         workDays: pureComputed(() => this.state.stagedAssignment().work_days),
         title: "Working Days",
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.WORK_DAY_EDITOR)!
               .validationText();
         }),
         requestSize: () => {},
      });
   };

   allocationTypeEditorFactory = (): ComponentArgs<AllocationTypeEditorElementParams> => {
      return AllocationTypeEditorElement.factory({
         onChange: (type: AllocationType) => {
            this.state.setAllocationType(type);
         },
         currentAllocationType: this.state.allocationType,
         title: "Allocation Type",
         requestSize: () => {},
      });
   };

   workHoursEditorFactory = (): ComponentArgs<WorkHoursEditorElementParams> => {
      return WorkHoursEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.WORK_HOURS_EDITOR)!
               .validationText();
         }),
         onStartTimeChange: (startTime) => {
            this.state.setStartTime(startTime);
         },
         onEndTimeChange: (endTime) => {
            this.state.setEndTime(endTime);
         },
         startTime: pureComputed(() => this.state.stagedAssignment().start_time),
         endTime: pureComputed(() => this.state.stagedAssignment().end_time),
         title: "Work Hours",
         requestSize: () => {},
      });
   };

   percentAllocationEditorFactory = (): ComponentArgs<PercentAllocationEditorElementParams> => {
      return PercentAllocationEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.PERCENT_ALLOCATION_EDITOR)!
               .validationText();
         }),
         onChange: (percentAllocation) => {
            this.state.setPercentAllocation(percentAllocation);
         },
         percentAllocation: pureComputed(() => this.state.stagedAssignment().percent_allocated),
         title: "Percent Allocated",
         requestSize: () => {},
      });
   };

   overtimeCheckboxEditorFactory = (): ComponentArgs<OvertimeCheckboxEditorElementParams> => {
      return OvertimeCheckboxEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.OVERTIME_CHECKBOX_EDITOR)!
               .validationText();
         }),
         isDisabled: false,
         onChange: (isUsingOvertime) => {
            this.state.setOvertimeCheckbox(isUsingOvertime);
         },
         value: pureComputed(() => this.state.stagedAssignment().overtime),
         title: "Overtime",
         requestSize: () => {},
      });
   };

   overtimePaysplitEditorFactory = (): ComponentArgs<OvertimePaysplitEditorElementParams> => {
      return OvertimePaysplitEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.OVERTIME_PAYSPLIT_EDITOR)!
               .validationText();
         }),
         isDisabled: false,
         onChange: (overtimePaysplit) => {
            this.state.updateOvertimePaysplit(overtimePaysplit);
         },
         value: pureComputed(() => this.state.stagedAssignment().pay_split ?? null),
         title: "Pay Split",
         requestSize: () => {},
      });
   };

   overtimeRatesEditorFactory = (): ComponentArgs<OvertimeRatesEditorElementParams> => {
      return OvertimeRatesEditorElement.factory({
         invalidInputMessage: pureComputed(() => {
            if (this.editorList == null) return null;
            return this.editorList
               .find((editor) => editor.name === AssignmentEditorName.OVERTIME_RATES_EDITOR)!
               .validationText();
         }),
         isDisabled: false,
         onChange: (overtimeRates) => {
            this.state.updateOvertimeRates(overtimeRates);
         },
         overtimeRates: pureComputed(() => this.state.stagedAssignment().overtime_rates ?? null),
         title: "Overtime Rates",
         requestSize: () => {},
      });
   };

   private readonly isAllValid = pureComputed(() => {
      return this.editorList
         .filter((editor) => editor.display() != "none" && editor.isUsed() === true)
         .every((editor) => editor.validationText() == null);
   });

   private readonly getSectionedEditorList: () => AssignmentEditorData[][] = () => [
      [
         {
            component: this.personEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.PERSON_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => {
               const resource_id = this.state.stagedAssignment().resource_id;
               return resource_id == null ? "Required" : null;
            }),
         },
         {
            component: this.projectEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.PROJECT_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => {
               const project_id = this.state.stagedAssignment().project_id;
               return project_id == null ? "Required" : null;
            }),
         },
         {
            component: this.costCodeEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.COST_CODE_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => null),
         },
         {
            component: this.costCodeLabelEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.COST_CODE_LABEL_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => null),
         },
         {
            component: this.statusEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.STATUS_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => {
               return authManager.checkAuthAction(PermissionLevel.Action.CAN_VIEW_ALL_STATUSES) !==
                  true && this.state.stagedAssignment().status_id == null
                  ? "Required"
                  : null;
            }),
         },
         {
            component: this.workDayEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.WORK_DAY_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => {
               return Object.values(this.state.stagedAssignment().work_days).every(
                  (isWorkDay) => !isWorkDay,
               )
                  ? "Must have at least one work day selected"
                  : null;
            }),
         },
      ],
      [
         {
            component: this.startDayEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.START_DAY_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => {
               const startDay = this.state.startDay();
               return startDay == null
                  ? "Required"
                  : !this.state.isWorkDay(startDay.getDay())
                  ? "Must be a work day"
                  : startDay.getTime() > (this.state.endDay()?.getTime() ?? 0)
                  ? "Start day must be before end day"
                  : null;
            }),
         },
         {
            component: this.endDayEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.END_DAY_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => {
               const endDay = this.state.endDay();
               const endDayOption = this.state.endDayOption();
               const endDayNumWeeks = this.state.endDayNumWeeks();
               return endDay == null
                  ? "Required"
                  : !this.state.isWorkDay(endDay.getDay())
                  ? "Must be a work day"
                  : (this.state.startDay()?.getTime() ?? 0) > endDay.getTime()
                  ? "End day must be after start day"
                  : endDayNumWeeks == null
                  ? "Must enter a number of weeks"
                  : endDayOption === EndDayOption.WEEKS && endDayNumWeeks > 4000
                  ? "Number of weeks cannot be over 4000"
                  : endDayOption === EndDayOption.WEEKS && endDayNumWeeks < 1
                  ? "Number of weeks cannot be less than 1"
                  : null;
            }),
         },
         {
            component: this.allocationTypeEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.ALLOCATION_TYPE_EDITOR,
            display: pureComputed<DisplayOption>(() => "block" as const),
            validationText: pureComputed<string | null>(() => null),
         },
         {
            component: this.workHoursEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.WORK_HOURS_EDITOR,
            display: pureComputed<DisplayOption>(() =>
               this.state.allocationType() === AllocationType.HOURS ? "block" : "none",
            ),
            validationText: pureComputed<string | null>(() => {
               const startTime = this.state.stagedAssignment().start_time;
               const endTime = this.state.stagedAssignment().end_time;
               return startTime == null || endTime == null
                  ? "Required"
                  : startTime < 0 || startTime > 23.75
                  ? "Invalid start time"
                  : endTime < 0 || endTime > 23.75
                  ? "Invalid end time"
                  : null;
            }),
         },
         {
            component: this.overtimeCheckboxEditorFactory(),
            isUsed: () => this.state.supportData.overtimeDayRates != null,
            name: AssignmentEditorName.OVERTIME_CHECKBOX_EDITOR,
            display: pureComputed<DisplayOption>(() =>
               this.state.allocationType() == AllocationType.HOURS ? "block" : "none",
            ),
            validationText: pureComputed<string | null>(() => null),
         },
         {
            component: this.overtimeRatesEditorFactory(),
            isUsed: () => this.state.supportData.overtimeDayRates != null,
            name: AssignmentEditorName.OVERTIME_RATES_EDITOR,
            display: pureComputed<DisplayOption>(() =>
               this.state.allocationType() == AllocationType.HOURS &&
               this.state.stagedAssignment().overtime == true
                  ? "block"
                  : "none",
            ),
            validationText: pureComputed<string | null>(() => {
               const overtime = this.state.stagedAssignment().overtime;
               const overtimeRates = this.state.stagedAssignment().overtime_rates;
               if (overtime == false) {
                  return overtimeRates != null
                     ? "Cannot specify overtime rates when overtime is off"
                     : null;
               }
               if (overtimeRates == null) {
                  return "Must specify overtime rates";
               }
               if (Object.values(overtimeRates).some((rate) => rate < 0)) {
                  return "Overtime rates cannot be negative";
               }
               return null;
            }),
         },
         {
            component: this.overtimePaysplitEditorFactory(),
            isUsed: () => this.state.supportData.overtimeDayRates != null,
            name: AssignmentEditorName.OVERTIME_PAYSPLIT_EDITOR,
            display: pureComputed<DisplayOption>(() =>
               this.state.allocationType() == AllocationType.HOURS &&
               this.state.stagedAssignment().overtime == true
                  ? "block"
                  : "none",
            ),
            validationText: pureComputed<string | null>(() => {
               const overtime = this.state.stagedAssignment().overtime;
               const paySplit = this.state.stagedAssignment().pay_split;
               if (overtime == false) {
                  return paySplit != null ? "Cannot specify pay splits when overtime is off" : null;
               }
               if (paySplit == null) {
                  return "Must specify pay splits";
               }
               const startTime = this.state.stagedAssignment().start_time!;
               const endTime = this.state.stagedAssignment().end_time!;
               const totalHours =
                  endTime >= startTime ? endTime - startTime : 24 + endTime - startTime;
               if (paySplit.overtime + paySplit.straight + paySplit.unpaid != totalHours) {
                  return `Total time must add up to ${totalHours} hours`;
               }
               return null;
            }),
         },
         {
            component: this.percentAllocationEditorFactory(),
            isUsed: () => true,
            name: AssignmentEditorName.PERCENT_ALLOCATION_EDITOR,
            display: pureComputed<DisplayOption>(() =>
               this.state.allocationType() === AllocationType.PERCENT ? "block" : "none",
            ),
            validationText: pureComputed<string | null>(() => {
               const percentAllocation = this.state.stagedAssignment().percent_allocated;
               return percentAllocation == null
                  ? "Required"
                  : percentAllocation <= 0
                  ? "Must be greater than zero"
                  : null;
            }),
         },
      ],
   ];

   static factory(params: AssignmentEditPaneParams): ComponentArgs<AssignmentEditPaneParams> {
      return {
         name: "assignment-edit-pane",
         params,
      };
   }
}

ko.components.register("assignment-edit-pane", {
   viewModel: AssignmentEditPane,
   template: template(),
   synchronous: true,
});
